import React, { useContext, useEffect, useState } from 'react';

import { PlaceOrderButton } from '../PlaceOrderButton';
import { PurchaseDetails } from '../PurchaseDetails';

import { TradingContext, TVariation } from '@trader/contexts';
import {
  calculateSpread,
  formatByPipSize,
  getMaxDigitWithDot,
} from '@trader/utils';
import { MODAL_TYPES } from '@trader/constants';
import { useMst } from '@trader/store';
import { useI18next } from '@trader/services';

import { BuyButton } from '../../../components/buyButton';
import { OrderInput } from '../../../components/orderInput';

import * as Styled from './styled';

interface ILimitOrder {
  buttonTitle?: string;
  isEditingOrder?: boolean;
  onClick?: () => void;
}

export const LimitOrder: React.FC<ILimitOrder> = ({
  buttonTitle,
  isEditingOrder = false,
  onClick,
}) => {
  const store = useMst();
  const { translate } = useI18next();
  const {
    instrument: { general, limit: state, market },
    isEditedFields,
    realTimePrice,
    errors,
    handleChangeStopLimit,
    isInsufficientFundsError,
  } = useContext(TradingContext);

  const [priceError, setPriceError] = useState('');

  const currentValue =
    state?.billType === 'Buy' ? state.buyValue : state.sellValue;
  const difference = general.stopsLevel * general.pipSize;

  const buyUpDiffValue = formatByPipSize(
    Number(realTimePrice.bid) + difference,
    general.pipSize
  );
  const buyDownDiffValue = formatByPipSize(
    Number(realTimePrice.bid) - difference,
    general.pipSize
  );
  const sellDownDiffValue = formatByPipSize(
    Number(realTimePrice.ask) - difference,
    general.pipSize
  );
  const sellUpDiffValue = formatByPipSize(
    Number(realTimePrice.ask) + difference,
    general.pipSize
  );

  const isPlaceStopLimitOrderDisable =
    !state.amount ||
    !!errors.limitAmountError ||
    !!priceError ||
    !general.symbol ||
    !state.amount ||
    !state?.billType ||
    isInsufficientFundsError;

  const handleChangePrice = (value: string, variation?: TVariation) => {
    const name = state?.billType === 'Buy' ? 'buyValue' : 'sellValue';
    if (Number(value) < 0) {
      return;
    }
    handleChangeStopLimit(value, name, 'stopLossError', variation);
  };

  const handleChangeAmount = (value: string, variation?: TVariation) => {
    handleChangeStopLimit(value, 'amount', 'limitAmountError', variation);
  };

  const addDeposit = () => {
    store.ui.modal.open(MODAL_TYPES.addFunds);
  };

  useEffect(() => {
    if (!Number(currentValue)) {
      setPriceError(`Should be higher than ${general.pipSize}`);
      return;
    }
    if (state?.billType === 'Buy') {
      if (
        Number(currentValue) > Number(buyDownDiffValue) &&
        Number(currentValue) < Number(buyUpDiffValue)
      ) {
        setPriceError(
          `Should be more than ${buyUpDiffValue} or less than ${buyDownDiffValue}`
        );
      } else {
        setPriceError('');
      }
    } else {
      if (
        Number(currentValue) > Number(sellDownDiffValue) &&
        Number(currentValue) < Number(sellUpDiffValue)
      ) {
        setPriceError(
          `Should be more than ${sellUpDiffValue} or less than ${sellDownDiffValue}`
        );
      } else {
        setPriceError('');
      }
    }
  }, [currentValue, realTimePrice.ask]);

  return (
    <React.Fragment>
      <Styled.Root>
        {!import.meta.env.VITE_IS_DMA && (
          <Styled.ActionButtons>
            <BuyButton
              isDisabled={isEditingOrder}
              order='Limit'
              isAsk
              type='Sell'
            />
            <Styled.Spread>
              {calculateSpread({
                ask: Number(realTimePrice.ask),
                bid: Number(realTimePrice.bid),
                pipSize: general.pipSize,
              })}
            </Styled.Spread>
            <BuyButton isDisabled={isEditingOrder} order='Limit' type='Buy' />
          </Styled.ActionButtons>
        )}

        <Styled.Main>
          <Styled.Title>
            {translate(`COMMON.LABELS.${state.billType?.toUpperCase()}_WHEN`)}
          </Styled.Title>
          <OrderInput
            placeholder='Price'
            disabled={false}
            minValue={0}
            maxValue={0}
            fixDigitAfterDot={getMaxDigitWithDot(general.pipSize)}
            step={general.pipSize}
            value={state?.billType === 'Buy' ? state.buyValue : state.sellValue}
            onChangeInput={handleChangePrice}
            errorMsg={
              state?.billType === 'Buy'
                ? (isEditedFields.limitBuy && priceError) || ''
                : (isEditedFields.limitSell && priceError) || ''
            }
          />
        </Styled.Main>
        <Styled.Main>
          <Styled.Title>{translate('COMMON.LABELS.AMOUNT')}</Styled.Title>
          <OrderInput
            placeholder={translate('COMMON.LABELS.AMOUNT')}
            disabled={isEditingOrder}
            minValue={market.minAmountValue}
            maxValue={market.maxAmountValue}
            fixDigitAfterDot={general.fixDigitAfterDot}
            step={general.step}
            value={state.amount}
            onChangeInput={handleChangeAmount}
            shouldCheckActivityOfButtons
            errorMsg={errors.limitAmountError}
          />
        </Styled.Main>
        <PurchaseDetails
          amountVolume={currentValue || '0'}
          addDeposit={addDeposit}
          symbol={general.symbol}
          quantity={state.amount}
          price={state?.billType === 'Buy' ? state.buyValue : state.sellValue}
          side={state.billType}
        />
      </Styled.Root>
      <PlaceOrderButton
        orderType='EntryLimit'
        isDisabled={isPlaceStopLimitOrderDisable}
        buttonTitle={buttonTitle}
        onClick={onClick}
      />
    </React.Fragment>
  );
};
